import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import {BehaviorSubject, Observable, of, switchMap} from "rxjs";
import {combineLatest} from "rxjs";
import {select, Store} from "@ngrx/store";
import * as userSettingsSelectors from "../../../user-settings/selectors/user-settings";
import {ApplicationState} from "../../../store/application-store";
import {DashboardScrollService} from "../../../dashboards/services/dashboard-scroll.service";
import {Direction} from "../../../common/enums/direction";
import {DeviceDetectorService} from "../../../common/device-detection/services/device-detector.service";
import {WidgetGalleryService} from "../../../widget-gallery/services/widget-gallery.service";
import {DashboardService} from "../../../dashboards/services/dashboard.service";
import {distinctUntilChanged, map, takeWhile} from "rxjs";
import {ProductService} from "../../services/product.service";
import {EntityDataLayoutService} from "@layout/layouts/entity-data/entity-data-layout.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "mojo-page-title",
  templateUrl: "./page-title.component.html",
})
export class PageTitleComponent
  implements OnInit, OnDestroy, OnChanges {

  @Input()
  title: string = undefined;
  @Input()
  showProductTitle: boolean = false;
  @Input()
  ref: string = undefined;

  isAlive: boolean = true;
  title$: Observable<string>;
  ref$: Observable<string>;
  scrollDirection$: Observable<Direction>;
  scrollDown: Direction = Direction.Down;
  isWidgetGalleryOpen$: Observable<boolean>;
  isInDashboardsMode: Observable<boolean>;
  titleUpdateSubject = new BehaviorSubject<string>(undefined);
  titleUpdate$ = this.titleUpdateSubject.asObservable();
  refUpdateSubject = new BehaviorSubject<string>(undefined);
  refUpdate$ = this.refUpdateSubject.asObservable();

  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly dashboardScrollService: DashboardScrollService,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly widgetGalleryService: WidgetGalleryService,
    private readonly dashboardService: DashboardService,
    private readonly productService: ProductService,
    private readonly entityDataLayoutService: EntityDataLayoutService) {
  }

  ngOnInit() {
    this.titleUpdateSubject.next(this.title);
    this.refUpdateSubject.next(this.ref);
    this.title$ = combineLatest([
      this.store.pipe(select(userSettingsSelectors.getPreviewMode)),
      this.productService.currentProductTitle(),
      this.titleUpdate$
    ]).pipe(
      map(([isInPreviewMode, currentProductTitle, titleUpdate], index) => {
        // ensure falsey string values are coalesced to ''
        // otherwise the words 'null' or 'undefined' can appear transitively!
        const fullTitle = this.showProductTitle && !!currentProductTitle
          ? `${titleUpdate ?? ''} - ${currentProductTitle}`
          : titleUpdate ?? '';
        return isInPreviewMode
          ? `${fullTitle} - Preview Mode`
          : `${fullTitle}`
      }),
      takeWhile(() => this.isAlive),
      distinctUntilChanged(),
    );
    this.ref$ = this.refUpdate$.pipe(
      takeWhile(() => this.isAlive),
      map(x => !!x ? ` Ref: ${x}` : ''),
      distinctUntilChanged(),
    );
    this.scrollDirection$ = this.dashboardScrollService.getScrollDirection();
    this.isWidgetGalleryOpen$ = this.widgetGalleryService.isGalleryOpen();
    this.isInDashboardsMode = this.store.select(userSettingsSelectors.getIsMyDashboardMode());
  }

  ngOnDestroy() {
    this.isAlive = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.title) {
      this.titleUpdateSubject.next(changes.title.currentValue);
    }
    if (changes.ref) {
      this.refUpdateSubject.next(changes.ref.currentValue);
    }
  }

  isMobile(): boolean {
    return this.deviceDetectorService.isMobile() ||
      this.deviceDetectorService.isTablet();
  }

  displayWidgetGalleryButton(): Observable<boolean> {
    return this.entityDataLayoutService.ActivatedRoute$.pipe(
      switchMap(x => this.dashboardService.canAddWidgets(x) ? of(true) : of(false))
    );
  }

  toggleWidgetSelector() {
    this.widgetGalleryService.toggle();
  }
}

